import { useGetChatMessages } from "@/hooks";
import { SocketManager } from "@/services";
import useChatStore from "@/store/chatStore";
import { CHAT_ROLE, SOCKET_EVENTS, SocketMessageDto } from "@/types";
import React, { useCallback, useEffect, useState } from "react";
import { NewMessageAlert } from "../../components/NewMessageAlert";
import PreviewAttachmentLightBox from "../../components/PreviewAttachmentLightBox";
import ChatInputWrap from "./ChatInputWrap";
import { ApiMessage, SocketMessage } from "./Message";

interface Props {
  roomId: number;
}

const ChatRoom: React.FC<Props> = ({ roomId }) => {
  // Global state to manage all chat functions
  const isConnectionReady = useChatStore((state) => state.isConnectionReady);
  const chatRole = useChatStore((state) => state.chatRole);
  const failConnectionMessage = useChatStore(
    (state) => state.failConnectionMessage
  );
  const setEditingMessage = useChatStore((state) => state.setEditingMessage);
  const resetRoomUnreadCount = useChatStore(
    (state) => state.resetRoomUnreadCount
  );

  // save scrollTop to check user scroll position
  const [scrollTop, setScrollTop] = useState(0);
  const [showNewMessageAlert, setShowNewMessageAlert] = useState(false);

  const [socketMessages, setSocketMessages] = useState<SocketMessageDto[]>([]);

  // Callback to handle data refresh from useGetChatMessages
  // Reset array of socket messages when new data is fetched
  const handleListMessageRefresh = useCallback(() => {
    setSocketMessages([]);
  }, []);

  // fetch messages by roomId from server
  const {
    messages,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetching,
    refetch,
  } = useGetChatMessages(roomId, { limit: 24 }, handleListMessageRefresh);

  // connect socket client to send and receive message in real-time
  useEffect(() => {
    // Reset state when roomId changes
    setScrollTop(0);
    setShowNewMessageAlert(false);
    setEditingMessage();

    // If connection is not ready, do not join room
    if (!isConnectionReady) return;

    const socketManager = SocketManager.getInstance();

    // join room
    socketManager.joinRoom(roomId.toString());

    // listen for new messages
    socketManager.onMessage(handleNewSocketMessage);

    // listen for message deleted
    socketManager.onMessageDeleted((data) => {
      // only update message for current room
      if (data.roomId != roomId.toString()) return;

      // reset api get list message
      refetch();
    });

    // listen for message edited
    socketManager.onMessageEdited((data) => {
      // only update message for current room
      if (data.roomId != roomId.toString()) return;

      // reset api get list message
      refetch();
    });

    return () => {
      // should emit event leave chat room
      socketManager.emitLeaveRoom(roomId.toString());

      // Tránh bị duplicate listeners khi component unmount và mount lại
      socketManager.off(SOCKET_EVENTS.NEW_MESSAGE);

      socketManager.off(SOCKET_EVENTS.MESSAGE_DELETED);

      socketManager.off(SOCKET_EVENTS.MESSAGE_EDITED);
    };
  }, [roomId, isConnectionReady]);

  // Handle new message recieved from socket
  const handleNewSocketMessage = useCallback(
    (message: SocketMessageDto) => {
      // only update message for current room
      if (message.roomId.toString() != roomId.toString()) return;

      // Update list message in room
      setSocketMessages((prevMessages) => [message, ...prevMessages]);

      // if user scrolltop, show new message alert
      setShowNewMessageAlert(true);
    },
    [roomId]
  );

  // handle scroll list message to call load more message
  const handleScrollListMessages: React.UIEventHandler<HTMLDivElement> =
    useCallback(
      (event) => {
        if (!hasNextPage || isLoading || isFetching) return;

        const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
        const topPadding = 12; // scroll to top 12px to call
        if (Math.abs(scrollTop) + clientHeight >= scrollHeight - topPadding) {
          // Load more messages when scrolled to the top
          fetchNextPage();
        }
      },
      [fetchNextPage, hasNextPage, isLoading, isFetching]
    );

  // handle scroll end to update scroll position
  const handleScrollEnd: React.UIEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      const { scrollTop } = event.currentTarget;
      // You can update global state or perform actions based on scroll position here
      setScrollTop(scrollTop);

      // If user scroll to bottom, hide new message alert
      if (scrollTop >= -5) {
        // allow a small threshold
        setShowNewMessageAlert(false);
      }
    },
    []
  );

  const handleMessageAreaClick = useCallback(() => {
    resetRoomUnreadCount(roomId);
  }, [roomId, resetRoomUnreadCount]);

  return (
    <div className="flex flex-col relative h-full">
      {/* Make relative to position absolute children dropzone */}
      <div
        id="list-chat-messages"
        className="flex flex-col-reverse flex-1 overflow-y-auto scroll-smooth thin-scrollbar visible-scrollbar"
        onClick={handleMessageAreaClick}
        onScroll={handleScrollListMessages}
        onScrollEnd={handleScrollEnd}
      >
        {socketMessages.map((message) => (
          <SocketMessage key={message.messageId} message={message} />
        ))}

        {messages.map((message) => (
          <ApiMessage key={message.id} message={message} />
        ))}
      </div>

      {/* isConnectionReady &&  */}
      {chatRole === CHAT_ROLE.INCHARGE && <ChatInputWrap roomId={roomId} />}

      {!!failConnectionMessage && (
        <div className="text-xs text-light_secondary text-center p-2">
          {failConnectionMessage}
        </div>
      )}

      {/* Button scroll to bottom for new messages */}
      {showNewMessageAlert && scrollTop < -5 && (
        <NewMessageAlert onClose={() => setShowNewMessageAlert(false)} />
      )}

      {/* Lightbox for attachment preview */}
      <PreviewAttachmentLightBox />
    </div>
  );
};

export default ChatRoom;
